// src/App.js
import React, { useState, useEffect } from 'react';
import AssistantScreen from './components/AssistantScreen';
import FuturisticDetails from './components/FuturisticDetails';
import { Typewriter } from 'react-simple-typewriter';
import { FaRobot, FaCalculator, FaCube, FaVrCardboard, FaChartLine, FaArrowRight } from 'react-icons/fa';
import './App.css'; // Ensure App.css is imported (you might put animation styles here)

function useRemoveEpicOverlay() {

  useEffect(() => {
    // Remove overlay function
    const removeOverlay = () => {
      const overlays = document.querySelectorAll('div[style*="z-index: 9999999"]');
      overlays.forEach(el => el.remove());
    };

    // Initial sweep (in case overlay is already there)
    removeOverlay();

    // Watch DOM for new overlays
    const observer = new MutationObserver(removeOverlay);
    observer.observe(document.body, { childList: true, subtree: true });

    // Cleanup
    return () => {
      observer.disconnect();
      removeOverlay();
    };
  }, []);
}

function App() {
  useRemoveEpicOverlay();

  const [isFuturisticDetailsOpen, setIsFuturisticDetailsOpen] = useState(false);
  const [selectedApartmentData, setSelectedApartmentData] = useState(null);

  // This index should match the "Visite Virtuelle" step in StepProgress
  // Steps: ['Projet', 'Profil', 'Financement', 'Résultat', 'Visite Virtuelle'] -> index 4
  const futuristicDetailsStepIndex = 4;

  // Index for the "Résultat" step
  const handleOpenFuturisticDetails = (data) => {
    setSelectedApartmentData(data);
    setIsFuturisticDetailsOpen(true);
  };

  const handleCloseFuturisticDetails = () => {
    setIsFuturisticDetailsOpen(false);
    // setStep(RESULTAT_STEP_INDEX); // Assuming RESULTAT_STEP_INDEX is accessible or passed down
    // Consider if you want to clear selectedApartmentData immediately
    // or after the panel has animated out. For simplicity, we clear it here.
    // setSelectedApartmentData(null); // Optional: panel will show loading if opened again before data set
  };

  return (
    <div className="h-screen w-screen bg-background-light text-text-dark flex items-center justify-center px-8 overflow-hidden">
      {/* Main 2-column grid */}
      <div className="grid grid-cols-2 w-full h-full relative">

        {/* --- LEFT COLUMN --- */}
        {/* This column will EITHER show the original video/typewriter OR be covered by FuturisticDetails */}
        <div className={`relative flex items-center justify-center overflow-hidden ${!isFuturisticDetailsOpen ? 'bg-golden-light' : ''}`}>
          {/* Original Video Background & Typewriter (Visible when FuturisticDetails is closed) */}
          {!isFuturisticDetailsOpen && (
  <>
    <video
      autoPlay
      loop
      muted
      playsInline
      className="absolute top-0 left-0 w-full h-full object-cover opacity-20"
    >
      <source
        src="https://storage.googleapis.com/unreal-estate-static-assets/home-video"
        type="video/mp4"
      />
    </video>

    <div className="relative z-10 flex flex-col items-center text-center px-6 py-8 bg-white/90 rounded-xl shadow-xl backdrop-blur-sm">
      <img
        src="/unreal-logo.png"
        alt="UnReal Estate"
        className="w-28 h-28 mb-4"
      />
      
      <h1 className="text-2xl font-semibold text-gray-800">
      L’IA au service de vos ventes,<br />
        <span className="text-blue-500">
          <Typewriter
            words={[' gagnez en efficacité.']}
            loop={1}
            cursor
            cursorStyle="_"
            typeSpeed={70}
            deleteSpeed={0}
          />
        </span>
      </h1>

      <p className="mt-4 text-md text-gray-700 max-w-md leading-relaxed">
       Offrez une expérience unique avec des agents IA, des maquettes 3D et une gestion connectée.
      </p>

      <ul className="mt-6 text-left space-y-4 text-gray-700 text-base max-w-md">
        <li className="flex items-start gap-3">
          <FaRobot className="text-gray-600 mt-1" />
          <span>Chatbot IA pour qualifier les clients en temps réel</span>
        </li>
        <li className="flex items-start gap-3">
          <FaCalculator className="text-gray-600 mt-1" />
          <span>Simulation bancaire rapide et fiable</span>
        </li>
        <li className="flex items-start gap-3">
          <FaCube className="text-gray-600 mt-1" />
          <span>Maquette 3D pour visualiser les projets avant construction</span>
        </li>
        <li className="flex items-start gap-3">
          <FaVrCardboard className="text-gray-600 mt-1" />
          <span>Visite virtuelle immersive adaptée à la VEFA</span>
        </li>
        <li className="flex items-start gap-3">
          <FaChartLine className="text-gray-600 mt-1" />
          <span>Intégration CRM pour un suivi automatisé</span>
        </li>
      </ul>
      <div className="mt-8 flex justify-end w-full">
        <div className="flex items-center gap-2 text-blue-600 font-medium animate-bounce cursor-pointer">
          <span>Testez le parcours</span>
          <FaArrowRight className="text-blue-600" />
        </div>
      </div>
    </div>
  </>
)}

          {/* FuturisticDetails Panel - This slides in from the left, overlaying/using the left column's space */}
          {/* It's always rendered for smoother animation, but visibility is controlled by CSS transform */}
          <FuturisticDetails
            isOpen={isFuturisticDetailsOpen}
            apartmentData={selectedApartmentData}
            onClose={handleCloseFuturisticDetails}
          />
        </div>

        {/* --- RIGHT COLUMN --- */}
        <div className="flex items-center justify-center">
          <AssistantScreen
            // Props for FuturisticDetails panel control
            onShouldOpenFuturisticDetails={handleOpenFuturisticDetails}
            futuristicDetailsStepIndex={futuristicDetailsStepIndex}
            // Prop to let AssistantScreen know if panel is open (optional, if its behavior needs to change)
            isFuturisticDetailsPanelOpen={isFuturisticDetailsOpen}
          />
        </div>
      </div>
    </div>
  );
}

export default App;
